import React, { useState, useCallback } from 'react'
import { useLanguage, useDateLocale } from '../LanguageContext'

const SERVICE_I18N_KEY = {
  private_aviation: 'servicePrivateAviation',
  partner_hotels:   'servicePartnerHotels',
  car_transfers:    'serviceCarTransfers',
  airport_services: 'serviceAirportServices',
}

const STATUS_CLASS = {
  submitted:   'db-badge--received',
  in_progress: 'db-badge--progress',
  confirmed:   'db-badge--confirmed',
  completed:   'db-badge--completed',
  cancelled:   'db-badge--cancelled',
}

const STATUS_LABEL_KEY = {
  submitted:   'status_submitted',
  in_progress: 'status_in_progress',
  confirmed:   'status_confirmed',
  completed:   'status_completed',
  cancelled:   'status_cancelled',
}

const TRAIL_STATUSES = ['submitted', 'in_progress', 'confirmed', 'completed']
const TRAIL_LABEL_KEYS = ['trail_received', 'trail_in_progress', 'trail_confirmed', 'trail_completed']

const SERVICE_FILTERS = [
  { id: 'all',              labelKey: 'filterAll' },
  { id: 'private_aviation', labelKey: 'filterAviation' },
  { id: 'partner_hotels',   labelKey: 'filterHotels' },
  { id: 'car_transfers',    labelKey: 'filterTransfers' },
  { id: 'airport_services', labelKey: 'filterAirport' },
]

const ROOM_PREF_KEY = {
  suite: 'roomSuite',
  connect: 'roomConnecting',
  highfloor: 'roomHighFloor',
  privacy: 'roomPrivacy',
}

const VEHICLE_KEY = {
  sedan: 'vehicleSedan',
  suv: 'vehicleSuv',
  van: 'vehicleVan',
  maybach: 'vehicleMaybach',
}

const AIRPORT_SVC_KEY = {
  arrival: 'svcArrivalTitle',
  departure: 'svcDepartureTitle',
  transfer: 'svcTransferTitle',
}

function buildDetail(service, payload) {
  if (!payload) return '—'
  switch (service) {
    case 'private_aviation': {
      const from = payload.origin?.code      || payload.origin?.city      || '?'
      const to   = payload.destination?.code || payload.destination?.city || '?'
      return `${from} → ${to}`
    }
    case 'partner_hotels': {
      const city    = payload.city    || payload.country || '?'
      const checkIn = payload.checkIn || ''
      return checkIn ? `${city} — ${checkIn}` : city
    }
    case 'car_transfers': {
      const from = payload.pickup  || '?'
      const to   = payload.dropoff || '?'
      return `${from} → ${to}`
    }
    case 'airport_services': {
      const airport = payload.airport      || '?'
      const flight  = payload.flightNumber || ''
      return flight ? `${airport} · ${flight}` : airport
    }
    default:
      return '—'
  }
}

function buildNotes(service, payload, t) {
  if (!payload) return '—'
  const lines = []

  switch (service) {
    case 'private_aviation':
      if (payload.cabinClass) {
        lines.push(payload.cabinClass === 'first' ? t('firstClass') : t('businessClass'))
      }
      if (payload.adults) {
        lines.push(`${payload.adults} ${payload.adults !== 1 ? t('adultPlural') : t('adultSingular')}`)
      }
      if (payload.children) {
        lines.push(`${payload.children} ${payload.children !== 1 ? t('childPlural') : t('childSingular')}`)
      }
      if (payload.infants) {
        lines.push(`${payload.infants} ${payload.infants !== 1 ? t('infantPlural') : t('infantSingular')}`)
      }
      if (payload.flexDates) lines.push(t('notesFlexible'))
      if (payload.specialReqs) lines.push(payload.specialReqs)
      break

    case 'partner_hotels':
      if (payload.checkIn && payload.checkOut) lines.push(`${payload.checkIn} → ${payload.checkOut}`)
      if (payload.guests) {
        lines.push(`${payload.guests} ${payload.guests !== 1 ? t('guestsPlural') : t('guestsSingular')}`)
      }
      if (payload.roomPrefs?.length) {
        lines.push(payload.roomPrefs.map(id => t(ROOM_PREF_KEY[id] || id)).join(', '))
      }
      if (payload.specialReqs) lines.push(payload.specialReqs)
      break

    case 'car_transfers':
      if (payload.date) lines.push(payload.date)
      if (payload.time) lines.push(t('notesPickup', { time: payload.time }))
      if (payload.passengers) {
        lines.push(`${payload.passengers} ${payload.passengers !== 1 ? t('passengerPlural') : t('passengerSingular')}`)
      }
      if (payload.vehicle) lines.push(t(VEHICLE_KEY[payload.vehicle] || 'vehicleSedan'))
      if (payload.specialReqs) lines.push(payload.specialReqs)
      break

    case 'airport_services':
      if (payload.selectedSvc?.length) {
        lines.push(payload.selectedSvc.map(id => t(AIRPORT_SVC_KEY[id] || id)).join(', '))
      }
      if (payload.passengers) {
        lines.push(`${payload.passengers} ${payload.passengers !== 1 ? t('passengerPlural') : t('passengerSingular')}`)
      }
      if (payload.date) lines.push(payload.date)
      if (payload.specialReqs) lines.push(payload.specialReqs)
      break

    default:
      break
  }

  return lines.filter(Boolean).join(' · ') || '—'
}

function RequestDetail({ request, onClose }) {
  const { t } = useLanguage()
  const dateLocale = useDateLocale()

  const formatDateTime = (isoString) => {
    if (!isoString) return '—'
    return new Date(isoString).toLocaleString(dateLocale, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const statusKey = request.status
  const labelKey = STATUS_LABEL_KEY[statusKey] || 'status_submitted'
  const statusLabel = t(labelKey)
  const trailIdx = TRAIL_STATUSES.indexOf(statusKey)

  return (
    <div className="rh-detail">
      <div className="rh-detail__header">
        <button className="cnt-back" onClick={onClose} aria-label={t('backToHistory')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('allRequests')}
        </button>
        <div className="rh-detail__meta">
          <span className="rh-detail__ref">REQ-{String(request.id).padStart(3, '0')}</span>
          <span className={`db-badge ${STATUS_CLASS[statusKey] ?? ''}`}>{statusLabel}</span>
        </div>
      </div>

      <div className="rh-detail__body">
        <p className="cnt-header__overline">{t('requestDetail')}</p>
        <h2 className="rh-detail__title">
          {SERVICE_I18N_KEY[request.service] ? t(SERVICE_I18N_KEY[request.service]) : request.service}
        </h2>
        <p className="rh-detail__subtitle">{buildDetail(request.service, request.payload)}</p>

        <div className="rh-detail__rows">
          <div className="rh-detail__row">
            <span className="rh-detail__key">{t('reference')}</span>
            <span className="rh-detail__val">REQ-{String(request.id).padStart(3, '0')}</span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">{t('submitted')}</span>
            <span className="rh-detail__val">{formatDateTime(request.submitted_at)}</span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">{t('status')}</span>
            <span className="rh-detail__val">
              <span className={`db-badge ${STATUS_CLASS[statusKey] ?? ''}`}>{statusLabel}</span>
            </span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">{t('details')}</span>
            <span className="rh-detail__val">{buildNotes(request.service, request.payload, t)}</span>
          </div>
        </div>

        <div className="rh-trail">
          {TRAIL_LABEL_KEYS.map((labelKey, i) => {
            const isDone = trailIdx >= 0 && i <= trailIdx
            const isCurrent = trailIdx === i
            return (
              <React.Fragment key={labelKey}>
                <div className={`rh-trail__step${isDone ? ' rh-trail__step--done' : ''}${isCurrent ? ' rh-trail__step--current' : ''}`}>
                  <div className="rh-trail__dot" aria-hidden="true" />
                  <span className="rh-trail__label">{t(labelKey)}</span>
                </div>
                {i < TRAIL_LABEL_KEYS.length - 1 && (
                  <div className={`rh-trail__line${isDone && i < trailIdx ? ' rh-trail__line--done' : ''}`} aria-hidden="true" />
                )}
              </React.Fragment>
            )
          })}
        </div>

        <p className="rh-detail__notice">
          {t('historyNotice')}
        </p>
      </div>
    </div>
  )
}

export default function RequestHistory({ onBack, requests = [], loading = false }) {
  const { t } = useLanguage()
  const dateLocale = useDateLocale()

  const formatDate = useCallback((isoString) => {
    if (!isoString) return '—'
    return new Date(isoString).toLocaleDateString(dateLocale, {
      day: '2-digit', month: 'short', year: 'numeric',
    })
  }, [dateLocale])

  const [filter,   setFilter]   = useState('all')
  const [selected, setSelected] = useState(null)

  if (selected) {
    return (
      <div className="db-page cnt-page cnt-page--wide">
        <RequestDetail request={selected} onClose={() => setSelected(null)} />
      </div>
    )
  }

  const filtered = requests.filter(r =>
    filter === 'all' || r.service === filter
  )

  const active = filtered.filter(r =>
    ['submitted', 'in_progress'].includes(r.status)
  )

  const previous = filtered.filter(r =>
    ['confirmed', 'completed', 'cancelled'].includes(r.status)
  )

  const serviceTitle = (id) => {
    if (id === 'all') return ''
    return SERVICE_I18N_KEY[id] ? t(SERVICE_I18N_KEY[id]) : id
  }

  return (
    <div className="db-page cnt-page cnt-page--wide">

      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label={t('back')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('dashboard')}
        </button>
        <div className="cnt-header__title-wrap">
          <p className="cnt-header__overline">{t('member')}</p>
          <h1 className="cnt-header__title">{t('historyTitle')}</h1>
        </div>
      </div>

      <div className="rh-filters" role="tablist" aria-label={t('filterByService')}>
        {SERVICE_FILTERS.map(f => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            className={`rh-filter${filter === f.id ? ' rh-filter--active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {loading && (
        <div className="db-requests-loading" style={{ marginTop: 40 }}>
          <span className="db-requests-loading__dot" />
          <span className="db-requests-loading__dot" />
          <span className="db-requests-loading__dot" />
        </div>
      )}

      {!loading && (
        <>
          {active.length > 0 && (
            <section className="rh-section" aria-label={t('activeSection')}>
              <p className="db-overline">{t('activeSection')}</p>
              <div className="rh-list">
                {active.map((req, i) => (
                  <button
                    key={req.id}
                    className="rh-row rh-row--active"
                    onClick={() => setSelected(req)}
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className="rh-row__left">
                      <span className="rh-row__service">
                        {SERVICE_I18N_KEY[req.service] ? t(SERVICE_I18N_KEY[req.service]) : req.service}
                      </span>
                      <span className="rh-row__detail">{buildDetail(req.service, req.payload)}</span>
                    </div>
                    <div className="rh-row__right">
                      <span className="rh-row__date">{formatDate(req.submitted_at)}</span>
                      <span className={`db-badge ${STATUS_CLASS[req.status] ?? ''}`}>
                        {t(STATUS_LABEL_KEY[req.status] || 'status_submitted')}
                      </span>
                      <span className="rh-row__caret" aria-hidden="true">›</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {previous.length > 0 && (
            <section className="rh-section" aria-label={t('previousSection')}>
              <p className="db-overline">{t('previousSection')}</p>
              <div className="rh-list">
                {previous.map((req, i) => (
                  <button
                    key={req.id}
                    className="rh-row"
                    onClick={() => setSelected(req)}
                    style={{ animationDelay: `${(active.length + i) * 50}ms` }}
                  >
                    <div className="rh-row__left">
                      <span className="rh-row__service">
                        {SERVICE_I18N_KEY[req.service] ? t(SERVICE_I18N_KEY[req.service]) : req.service}
                      </span>
                      <span className="rh-row__detail">{buildDetail(req.service, req.payload)}</span>
                    </div>
                    <div className="rh-row__right">
                      <span className="rh-row__date">{formatDate(req.submitted_at)}</span>
                      <span className={`db-badge ${STATUS_CLASS[req.status] ?? ''}`}>
                        {t(STATUS_LABEL_KEY[req.status] || 'status_submitted')}
                      </span>
                      <span className="rh-row__caret" aria-hidden="true">›</span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {active.length === 0 && previous.length === 0 && (
            <div className="rh-empty">
              <p className="rh-empty__text">
                {filter === 'all'
                  ? t('noRequestsYetAll')
                  : t('noRequestsForService', { service: serviceTitle(filter) })}
              </p>
            </div>
          )}
        </>
      )}

    </div>
  )
}
